import { useEffect, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

function NotifyModal({ onClose }: { onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d6iqln1bfjp31emakb20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[#111111] border border-gray-700 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-notification-3-line text-teal-400 text-lg"></i>
            <h3 className="text-white font-bold text-base">오픈 예정 알림 신청</h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="px-5 py-5">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-check-line text-teal-400 text-2xl"></i>
              </div>
              <p className="text-white font-bold text-base mb-1">신청 완료!</p>
              <p className="text-gray-400 text-sm">오픈 시 이메일로 알려드릴게요 😊</p>
              <button onClick={onClose} className="mt-4 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap">
                닫기
              </button>
            </div>
          ) : (
            <>
              <div className="mb-4 bg-white/5 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-lock-line text-gray-500 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-gray-400 text-xs leading-relaxed">
                  <span className="text-white font-semibold">Smart Plan · Pro Plan</span> 오픈 시 가장 먼저 알림을 받아보세요!
                </p>
              </div>
              <form id="notify-form" data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">이름</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="이름을 입력해주세요"
                    className="w-full bg-white/5 border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-teal-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">이메일</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="이메일을 입력해주세요"
                    className="w-full bg-white/5 border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-teal-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">관심 플랜</label>
                  <select
                    name="plan"
                    required
                    className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500 transition-colors cursor-pointer"
                  >
                    <option value="">선택해주세요</option>
                    <option value="Smart Plan">Smart Plan</option>
                    <option value="Pro Plan">Pro Plan</option>
                    <option value="Smart Plan · Pro Plan 모두">Smart Plan · Pro Plan 모두</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold py-3 rounded-xl text-sm transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? '신청 중...' : '알림 신청하기'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentModal({ onClose, onShowHowTo }: { onClose: () => void; onShowHowTo: () => void }) {
  const [copied, setCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [selectedPack, setSelectedPack] = useState<'core' | 'double' | 'master'>('double');
  const [depositorName, setDepositorName] = useState('');
  const [showComplete, setShowComplete] = useState(false);

  const vatRate = 1;

  // 기간제 옵션 가격 기준:
  // 코어팩(1개월) = ₩59,000
  // 더블팩(3개월) = ₩59,000 × 3 × 0.95 → 월 ₩56,050 (5% 할인)
  // 마스터팩(6개월) = ₩59,000 × 6 × 0.90 → 월 ₩53,100 (10% 할인)
  const coreBasePrice = 59000;
  const doubleBasePrice = Math.round(59000 * 0.95); // 56,050
  const masterBasePrice = Math.round(59000 * 0.90); // 53,100

  const packs = {
    core: {
      id: 'core',
      name: 'LEXO 코어팩',
      subtitle: 'Vol.1',
      label: '단품',
      badge: null,
      monthlyPrice: coreBasePrice,
      totalPrice: coreBasePrice * 1,
      vatPrice: Math.round(coreBasePrice * 1 * vatRate),
      months: 1,
      discount: null,
      color: 'teal',
      icon: 'ri-book-open-line',
      duration: '첫 열람부터 1개월간 열람',
      versions: [
        { vol: 'Vol.1', open: '결제 확인 후 노션 링크 발송', duration: '첫 열람부터 1개월간 열람 가능' },
      ],
      features: ['Vol.1 노션 링크 제공', '밤 12시 전후 기준 발송', '첫 열람부터 1개월간 열람 가능'],
    },
    double: {
      id: 'double',
      name: 'LEXO 더블팩',
      subtitle: 'Vol.1+2',
      label: '추천',
      badge: '추천',
      monthlyPrice: doubleBasePrice,
      totalPrice: doubleBasePrice * 3,
      vatPrice: Math.round(doubleBasePrice * 3 * vatRate),
      months: 3,
      discount: '5% 할인',
      color: 'orange',
      icon: 'ri-stack-line',
      duration: '첫 열람부터 2개월간 열람',
      versions: [
        { vol: 'Vol.1', open: '결제 확인 후 노션 링크 발송', duration: '첫 열람부터 2개월간 열람 가능' },
        { vol: 'Vol.2', open: 'Vol.1 수강 개시 후 2주 후부터 노션 링크 발송', duration: '첫 열람부터 2개월간 열람 가능' },
      ],
      features: ['Vol.1+2 노션 링크 순차 제공', 'Vol.2는 Vol.1 개시 2주 후 발송', '첫 열람부터 2개월간 열람 가능'],
    },
    master: {
      id: 'master',
      name: 'LEXO 마스터팩',
      subtitle: 'Vol.1+2+3',
      label: 'VIP',
      badge: 'VIP',
      monthlyPrice: masterBasePrice,
      totalPrice: masterBasePrice * 6,
      vatPrice: Math.round(masterBasePrice * 6 * vatRate),
      months: 6,
      discount: '10% 할인',
      color: 'amber',
      icon: 'ri-vip-crown-line',
      duration: '첫 열람부터 3개월간 열람',
      versions: [
        { vol: 'Vol.1', open: '결제 확인 후 노션 링크 발송', duration: '첫 열람부터 3개월간 열람 가능' },
        { vol: 'Vol.2', open: 'Vol.1 수강 개시 후 2주 후부터 노션 링크 발송', duration: '첫 열람부터 3개월간 열람 가능' },
        { vol: 'Vol.3', open: 'Vol.1 수강 개시 후 1달 후부터 노션 링크 발송', duration: '첫 열람부터 3개월간 열람 가능' },
      ],
      features: ['Vol.1+2+3 노션 링크 순차 제공', 'Vol.2는 2주 후, Vol.3는 1달 후 발송', '첫 열람부터 3개월간 열람 가능'],
    },
  };

  const selected = packs[selectedPack];
  const formattedVatPrice = `₩${selected.vatPrice.toLocaleString()}`;
  const formattedMonthlyPrice = `₩${selected.monthlyPrice.toLocaleString()}`;
  const formattedTotalPrice = `₩${selected.totalPrice.toLocaleString()}`;

  const colorMap: Record<string, { border: string; bg: string; text: string; badge: string; btn: string; ring: string }> = {
    teal:   { border: 'border-teal-500',   bg: 'bg-teal-500/10',   text: 'text-teal-400',   badge: 'bg-teal-500 text-white',   btn: 'from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700', ring: 'ring-teal-500' },
    orange: { border: 'border-orange-500', bg: 'bg-orange-500/10', text: 'text-orange-400', badge: 'bg-orange-500 text-white', btn: 'from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600', ring: 'ring-orange-500' },
    amber:  { border: 'border-amber-400',  bg: 'bg-amber-500/10',  text: 'text-amber-400',  badge: 'bg-amber-400 text-black',  btn: 'from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600', ring: 'ring-amber-400' },
  };

  const handleCopy = () => {
    navigator.clipboard.writeText('110621532440');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {showNotify && <NotifyModal onClose={() => setShowNotify(false)} />}
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto pt-16 pb-4 px-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
        <div className="relative bg-[#111111] border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden my-auto">

          {/* 헤더 */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-bank-card-line text-white text-lg"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg leading-tight">수강 신청 안내</h2>
                  <p className="text-teal-100 text-sm">무통장 입금으로 간편하게 시작하세요</p>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Basic 플랜 강조 배너 */}
            <div className="mt-3 bg-white/20 border border-white/30 rounded-xl px-4 py-3 flex items-center justify-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-vip-crown-2-line text-white text-base"></i>
              </div>
              <div className="text-center">
                <p className="text-white font-black text-xl tracking-wide">LEXO Basic 플랜</p>
                <p className="text-teal-100 text-sm font-medium">현재 구매 가능한 플랜</p>
              </div>
            </div>

            {/* Basic 플랜 안내 + 할인 배너 */}
            <div className="mt-2 flex flex-col gap-1.5">
              <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
                <i className="ri-lock-line text-teal-100 text-sm flex-shrink-0"></i>
                <p className="text-teal-50 text-sm leading-snug">Smart · Pro 플랜은 준비 중입니다.</p>
              </div>
              <div className="flex items-center gap-2 bg-orange-500/30 border border-orange-400/40 rounded-lg px-3 py-2">
                <i className="ri-flashlight-fill text-orange-300 text-sm flex-shrink-0"></i>
                <p className="text-orange-100 text-sm leading-snug">
                  <span className="font-black text-white">이벤트 할인가 적용 중</span> · 정상가 ₩78,000 → <span className="text-orange-300 font-black">₩59,000</span> (부가세 별도)
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">

            {/* 패키지 선택 */}
            <div>
              <p className="text-gray-300 font-semibold text-base flex items-center gap-2 mb-3">
                <i className="ri-gift-line text-teal-400"></i>
                패키지 선택
                <span className="text-xs text-gray-400 font-semibold bg-white/10 px-2 py-0.5 rounded-full whitespace-nowrap">전 패키지 이벤트가 적용</span>
              </p>
              <div className="grid grid-cols-3 gap-2">
                {(Object.values(packs) as typeof packs[keyof typeof packs][]).map((pack) => {
                  const c = colorMap[pack.color];
                  const isSelected = selectedPack === pack.id;
                  return (
                    <button
                      key={pack.id}
                      onClick={() => setSelectedPack(pack.id as 'core' | 'double' | 'master')}
                      className={`relative flex flex-col items-center justify-center py-4 px-2 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                        isSelected ? `${c.border} ${c.bg}` : 'border-gray-700 bg-white/5 hover:border-gray-500'
                      }`}
                    >
                      {pack.badge && (
                        <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs font-black px-2 py-0.5 rounded-full whitespace-nowrap ${c.badge}`}>
                          {pack.badge}
                        </span>
                      )}
                      <div className={`w-8 h-8 flex items-center justify-center mb-1.5 ${isSelected ? c.text : 'text-gray-500'}`}>
                        <i className={`${pack.icon} text-xl`}></i>
                      </div>
                      <span className={`font-bold text-sm text-center leading-tight ${isSelected ? 'text-white' : 'text-gray-400'}`}>{pack.name}</span>
                      <span className={`text-xs font-semibold mt-0.5 ${isSelected ? c.text : 'text-gray-600'}`}>{pack.subtitle}</span>
                      <span className={`text-xs mt-1 ${isSelected ? 'text-gray-300' : 'text-gray-600'}`}>{pack.duration}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 선택된 패키지 상세 */}
            <div className={`rounded-xl border ${colorMap[selected.color].border} ${colorMap[selected.color].bg} p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <i className={`${selected.icon} ${colorMap[selected.color].text} text-lg`}></i>
                <span className={`font-bold text-base ${colorMap[selected.color].text}`}>{selected.name} {selected.subtitle}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colorMap[selected.color].badge}`}>{selected.label}</span>
              </div>

              {/* 버전별 발송 일정 */}
              <div className="space-y-2">
                {selected.versions.map((v, i) => (
                  <div key={i} className="bg-black/30 rounded-lg px-3 py-2.5 flex items-start gap-2.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${colorMap[selected.color].bg} border ${colorMap[selected.color].border}`}>
                      <span className={`text-xs font-black ${colorMap[selected.color].text}`}>{i + 1}</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-bold mb-0.5">{v.vol}</p>
                      <p className="text-gray-400 text-sm leading-relaxed">{v.open}</p>
                      <p className={`text-sm font-semibold mt-0.5 ${colorMap[selected.color].text}`}>{v.duration}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 입금 금액 */}
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-gray-400 text-sm">입금 금액 (부가세 포함)</p>
                <span className="text-xs font-bold text-gray-400 bg-white/10 border border-gray-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                  <i className="ri-price-tag-3-line mr-1"></i>이벤트 할인가
                </span>
              </div>

              {/* 가격 상세 */}
              <div className="space-y-1.5 mb-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">단가 (부가세 별도)</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-600 text-sm line-through">₩78,000</span>
                    <span className="text-gray-300 text-sm font-semibold">{formattedMonthlyPrice}</span>
                  </div>
                </div>
                {selected.discount && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">할인</span>
                    <span className="text-green-400 text-sm font-semibold">{selected.discount} 적용</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">소계 (부가세 별도)</span>
                  <span className="text-gray-300 text-sm font-semibold">{formattedTotalPrice}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">부가세 (VAT 10%)</span>
                  <span className="text-gray-300 text-sm font-semibold">+₩{Math.round(selected.totalPrice * 0.1).toLocaleString()}</span>
                </div>
              </div>

              <div className="border-t border-teal-500/20 pt-3">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <span className={`font-bold text-base ${colorMap[selected.color].text}`}>최종 입금 금액</span>
                  <span className="text-3xl font-black text-white">{formattedVatPrice}</span>
                </div>
                <p className="text-teal-400/70 text-xs mt-1.5 flex items-center gap-1">
                  <i className="ri-information-line text-xs"></i>
                  부가세(VAT 10%) 포함 최종 입금 금액
                </p>
              </div>
            </div>

            {/* 계좌 정보 */}
            <div className="bg-white/5 rounded-xl p-4 space-y-3">
              <p className="text-gray-300 font-semibold text-base flex items-center gap-2">
                <i className="ri-bank-line text-teal-400"></i>
                입금 계좌 정보
              </p>
              <div className="space-y-2.5">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">은행</span>
                  <span className="text-white text-sm font-semibold">신한은행</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">예금주</span>
                  <span className="text-white text-sm font-semibold">이송희</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">계좌번호</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-semibold tracking-wide">110621532440</span>
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-1 bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 text-xs px-2 py-1 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                      {copied ? '복사됨' : '복사'}
                    </button>
                  </div>
                </div>
              </div>

              {/* 입금자명 입력 */}
              <div className="pt-2 border-t border-gray-700/50">
                <label className="text-gray-400 text-sm mb-1.5 block flex items-center gap-1">
                  <i className="ri-user-line text-teal-400"></i>
                  입금자명
                </label>
                <input
                  type="text"
                  value={depositorName}
                  onChange={(e) => setDepositorName(e.target.value)}
                  placeholder="입금자 이름을 입력하세요"
                  className="w-full bg-[#0F0F0F] border border-gray-700 focus:border-teal-500 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none transition-colors"
                />
              </div>
            </div>

            {/* 진행 순서 */}
            <div className="space-y-2">
              <p className="text-gray-300 font-semibold text-base flex items-center gap-2">
                <i className="ri-list-ordered text-teal-400"></i>
                진행 순서
              </p>
              <div className="space-y-2">
                {[
                  { step: '1', text: `위 계좌로 ${formattedVatPrice} 입금`, icon: 'ri-bank-card-line' },
                  { step: '2', text: 'LEXO 일본어 카카오톡 채널 추가 후 입금 완료 연락', icon: 'ri-kakao-talk-fill' },
                  { step: '3', text: '확인 후 밤 12시 전후 기준으로 학습 링크 발송', icon: 'ri-notion-fill' },
                ].map((item) => (
                  <div key={item.step} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">{item.step}</span>
                    </div>
                    <span className="text-gray-300 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 카카오톡 안내 메시지 */}
            <div className="bg-[#FEE500]/10 border border-[#FEE500]/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <i className="ri-kakao-talk-fill text-[#FEE500] text-base"></i>
                  <span className="text-[#FEE500] font-bold text-sm">카카오톡 채널 입금 안내 메시지 예시</span>
                </div>
                <button
                  onClick={() => {
                    const msg = `안녕하세요! LEXO 일본어 수강 신청합니다 😊\n\n플랜: LEXO Basic 플랜\n패키지: ${selected.name} ${selected.subtitle}\n입금 금액: ${formattedVatPrice}\n입금자명: ${depositorName || '(입금자명 입력 후 자동 반영)'}\n\n입금 완료했습니다. 확인 부탁드립니다!`;
                    navigator.clipboard.writeText(msg);
                    setMessageCopied(true);
                    setTimeout(() => setMessageCopied(false), 2000);
                  }}
                  className="flex items-center gap-1 bg-[#FEE500]/20 hover:bg-[#FEE500]/40 text-[#3A1D1D] text-xs px-2.5 py-1 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={messageCopied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {messageCopied ? '복사됨 ✓' : '메시지 복사'}
                </button>
              </div>
              <div className="bg-black/30 rounded-lg p-3 font-mono text-sm text-gray-200 leading-relaxed whitespace-pre-line">
                {`안녕하세요! LEXO 일본어 수강 신청합니다 😊\n\n플랜: LEXO Basic 플랜\n패키지: ${selected.name} ${selected.subtitle}\n입금 금액: ${formattedVatPrice}\n입금자명: ${depositorName || '(입금자명 입력 후 자동 반영)'}\n\n입금 완료했습니다. 확인 부탁드립니다!`}
              </div>
              <p className="text-gray-500 text-xs mt-2">* 위 내용을 복사하여 카카오톡 채널에 보내주세요</p>
            </div>

            <a
              href="http://pf.kakao.com/_exilzX"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setTimeout(() => setShowComplete(true), 500)}
              className="flex items-center justify-center gap-2 w-full py-4 bg-[#FEE500] hover:bg-[#F5DC00] text-[#3A1D1D] font-bold rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap text-base"
            >
              <i className="ri-kakao-talk-fill text-lg"></i>
              LEXO 일본어 카카오톡 채널 추가하기
            </a>

            {/* 환불 정책 */}
            <div className="bg-white/5 border border-gray-700/50 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-information-line text-gray-400 text-xs"></i>
                <span className="text-gray-400 font-semibold text-xs">환불 정책 및 주의사항</span>
              </div>
              <ul className="space-y-1">
                {[
                  '결제 후 7일 이내, 콘텐츠 미사용 시 환불 가능',
                  '노션 링크 열람 후에는 환불이 불가합니다',
                  '환불 금액은 실제 결제 금액에서 이벤트 할인 혜택분을 제외한 정상가 기준으로 산정됩니다 (할인 혜택은 환불 대상에서 제외)',
                  '부가세(VAT 10%) 및 결제 수수료는 환불되지 않습니다',
                  '이용 기간에 비례하여 잔여 기간분만 환불됩니다 (일할 계산)',
                  '문의: lexojp.help@gmail.com',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-gray-600 text-[11px] mt-0.5 flex-shrink-0">•</span>
                    <span className="text-[11px] leading-relaxed text-gray-500">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={() => setShowNotify(true)}
              className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-gray-600 hover:border-teal-500/50 text-gray-300 hover:text-teal-300 text-sm py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-notification-3-line text-sm"></i>
              Smart Plan · Pro Plan 오픈 알림 신청
              <i className="ri-arrow-right-s-line text-sm"></i>
            </button>
          </div>
        </div>
      </div>

      {/* 완료 화면 */}
      {showComplete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => { setShowComplete(false); onClose(); }}></div>
          <div className="relative bg-[#111111] border border-teal-500/40 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600"></div>
            <div className="px-6 py-8 text-center">
              <div className="relative w-20 h-20 mx-auto mb-5">
                <div className="absolute inset-0 bg-teal-500/20 rounded-full animate-ping"></div>
                <div className="relative w-20 h-20 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <i className="ri-check-line text-white text-3xl"></i>
                </div>
              </div>
              <h3 className="text-white font-black text-xl mb-2">수강 신청 완료! 🎉</h3>
              <p className="text-teal-400 font-semibold text-sm mb-5">LEXO 일본어와 함께 강해지세요!</p>

              {/* 플랜 & 옵션 강조 카드 */}
              <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border-2 border-teal-500/50 rounded-2xl p-4 mb-4 text-left">
                <p className="text-teal-400 text-xs font-bold uppercase tracking-widest mb-3 text-center">선택하신 플랜 &amp; 옵션</p>
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-vip-crown-2-line text-teal-400 text-lg"></i>
                    <span className="text-white font-black text-xl">LEXO Basic 플랜</span>
                  </div>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${colorMap[selected.color].bg} border ${colorMap[selected.color].border}`}>
                    <i className={`${selected.icon} ${colorMap[selected.color].text} text-base`}></i>
                    <span className={`font-black text-base ${colorMap[selected.color].text}`}>{selected.name}</span>
                    <span className={`text-sm font-bold ${colorMap[selected.color].text} opacity-80`}>{selected.subtitle}</span>
                    <span className={`text-xs font-black px-2 py-0.5 rounded-full ${colorMap[selected.color].badge}`}>{selected.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <i className="ri-time-line text-gray-400 text-xs"></i>
                    <span className="text-gray-300 text-sm font-semibold">{selected.duration}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 mb-5 text-left space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">입금 금액</span>
                  <span className="text-teal-400 text-base font-black">{formattedVatPrice}</span>
                </div>
                {depositorName && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">입금자명</span>
                    <span className="text-white text-sm font-semibold">{depositorName}</span>
                  </div>
                )}
              </div>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4 mb-5 text-left">
                <p className="text-teal-400 font-bold text-sm mb-2 flex items-center gap-1.5">
                  <i className="ri-arrow-right-circle-line"></i>
                  다음 단계
                </p>
                <ol className="space-y-2">
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <span className="text-teal-500 font-bold flex-shrink-0">1.</span>
                    신한은행 <span className="text-white font-semibold">110621532440</span> (이송희)으로 입금
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <span className="text-teal-500 font-bold flex-shrink-0">2.</span>
                    카카오톡 채널에 입금 완료 메시지 전송
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <span className="text-teal-500 font-bold flex-shrink-0">3.</span>
                    확인 후 <span className="text-teal-400 font-semibold">밤 12시 전후</span> 학습 링크 발송!
                  </li>
                </ol>
              </div>
              <p className="text-gray-500 text-xs mb-5">
                문의: <span className="text-gray-400">lexojp.help@gmail.com</span>
              </p>
              <button
                onClick={() => { setShowComplete(false); onClose(); }}
                className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold py-3.5 rounded-xl text-base transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                확인했습니다 👍
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 학습 방식 상세 섹션 */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-transparent via-teal-500/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-teal-500/20 text-teal-400 px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
              학습 방식
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
              이렇게 공부해요
            </h2>
            <p className="text-gray-400 text-sm sm:text-lg">결제부터 숙제 제출까지 한눈에</p>
          </div>

          {/* 플로우 카드 */}
          <div className="relative">
            {/* 연결선 (데스크탑) */}
            <div className="hidden sm:block absolute left-[39px] top-12 bottom-12 w-0.5 bg-gradient-to-b from-teal-500/60 via-teal-500/30 to-teal-500/10 z-0"></div>

            <div className="space-y-4 sm:space-y-6 relative z-10">

              {/* Step 1 */}
              <div className="flex items-start gap-4 sm:gap-6">
                <div className="flex-shrink-0 w-10 h-10 sm:w-20 sm:h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <i className="ri-bank-card-line text-white text-base sm:text-2xl"></i>
                </div>
                <div className="flex-1 bg-white/5 border border-gray-800 hover:border-teal-500/40 rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                    <span className="text-teal-400 text-xs font-bold bg-teal-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">STEP 1</span>
                    <h3 className="text-sm sm:text-xl font-bold text-white">수강료 송금</h3>
                  </div>
                  <p className="text-gray-400 text-xs sm:text-base leading-relaxed mb-3">
                    신한은행 계좌로 수강료를 입금합니다.
                  </p>
                  <div className="bg-black/30 rounded-lg px-3 py-2.5 flex items-start gap-2.5">
                    <div className="flex items-center gap-2">
                      <i className="ri-bank-line text-teal-400 text-xs flex-shrink-0"></i>
                      <span className="text-gray-300 text-xs sm:text-sm">신한은행 <span className="text-white font-semibold">110621532440</span> (예금주: 이송희)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-information-line text-teal-400 text-xs flex-shrink-0"></i>
                      <span className="text-gray-300 text-xs sm:text-sm">입금자명을 본인 이름으로 정확히 입력해주세요</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-4 sm:gap-6">
                <div className="flex-shrink-0 w-10 h-10 sm:w-20 sm:h-20 bg-gradient-to-br from-[#FEE500] to-[#F5DC00] rounded-full flex items-center justify-center shadow-lg shadow-yellow-500/20">
                  <i className="ri-kakao-talk-fill text-[#3A1D1D] text-base sm:text-2xl"></i>
                </div>
                <div className="flex-1 bg-white/5 border border-gray-800 hover:border-yellow-500/40 rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                    <span className="text-yellow-400 text-xs font-bold bg-yellow-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">STEP 2</span>
                    <h3 className="text-sm sm:text-xl font-bold text-white">카카오 채널 추가 &amp; 입금 알림</h3>
                  </div>
                  <p className="text-gray-400 text-xs sm:text-base leading-relaxed mb-3">
                    LEXO 일본어 카카오 채널을 추가하고, 입금 완료 메시지를 보내주세요.
                  </p>
                  <div className="bg-[#FEE500]/10 border border-[#FEE500]/20 rounded-lg px-3 py-2.5 space-y-1.5">
                    <div className="flex items-start gap-2">
                      <i className="ri-chat-1-line text-yellow-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs sm:text-sm">카카오톡에서 <span className="text-white font-semibold">"LEXO 일본어"</span> 채널 검색 후 추가</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-message-2-line text-yellow-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs sm:text-sm">채널에 <span className="text-white font-semibold">이름 / 선택 플랜 / 입금 금액</span>을 메시지로 전송</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-time-line text-yellow-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs sm:text-sm">입금 확인은 영업일 기준으로 처리됩니다</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 - 학습 진행 과정 섹션 (상단) */}
              <div className="relative group">
                <div className="absolute -top-2 sm:-top-4 -left-1 sm:-left-4 w-6 h-6 sm:w-12 sm:h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-xl z-10">
                  3
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300">
                  <div className="w-full h-20 sm:h-48 overflow-hidden" style={{ minHeight: '80px' }}>
                    <img 
                      src="https://public.readdy.ai/ai/img_res/e3d7740fd818f91657593ecbdfe9fae6.jpg"
                      alt="학습 링크 페이지 예시"
                      loading="eager"
                      crossOrigin="anonymous"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      style={{ display: 'block', width: '100%', height: '100%' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                  <div className="p-2 sm:p-6">
                    <h3 className="text-xs sm:text-xl font-bold text-white mb-1 sm:mb-2">학습 시작</h3>
                    <p className="text-gray-400 text-[10px] sm:text-[15px] hidden sm:block">학습 링크가 발송되면 바로 학습을 시작하세요</p>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start gap-4 sm:gap-6">
                <div className="flex-shrink-0 w-10 h-10 sm:w-20 sm:h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg shadow-red-500/20">
                  <i className="ri-youtube-fill text-white text-base sm:text-2xl"></i>
                </div>
                <div className="flex-1 bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/40 rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                    <span className="text-red-400 text-xs font-bold bg-red-500/20 px-2 py-0.5 rounded-full whitespace-nowrap">STEP 4</span>
                    <h3 className="text-sm sm:text-xl font-bold text-white">유튜브 영상 &amp; 핵심 표현 학습</h3>
                  </div>
                  <p className="text-gray-400 text-xs sm:text-base leading-relaxed mb-3">
                    노션에서 유튜브 영상 링크와 핵심 표현을 확인하고 학습합니다.
                  </p>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5 space-y-1.5">
                    <div className="flex items-start gap-2">
                      <i className="ri-play-circle-line text-red-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs sm:text-sm"><span className="text-white font-semibold">유튜브 영상</span>을 반복 시청하며 전체 흐름을 파악합니다</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-book-2-line text-red-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs sm:text-sm">노션에 정리된 <span className="text-white font-semibold">핵심 표현과 대사</span>를 꼼꼼히 읽고 의미를 파악합니다</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-repeat-line text-red-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs sm:text-sm"><span className="text-white font-semibold">따라 말하기(쉐도잉)</span>를 반복 연습합니다</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex items-start gap-4 sm:gap-6">
                <div className="flex-shrink-0 w-10 h-10 sm:w-20 sm:h-20 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <i className="ri-mic-line text-white text-base sm:text-2xl"></i>
                </div>
                <div className="flex-1 bg-gradient-to-br from-teal-500/10 to-teal-600/5 border border-teal-500/40 rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                    <span className="text-teal-300 text-xs font-bold bg-teal-500/20 px-2 py-0.5 rounded-full whitespace-nowrap">STEP 5</span>
                    <h3 className="text-sm sm:text-xl font-bold text-white">쉐도잉 녹음 &amp; 숙제 제출</h3>
                  </div>
                  <p className="text-gray-300 text-xs sm:text-base leading-relaxed mb-3">
                    학습한 내용을 쉐도잉하여 녹음한 뒤, <span className="text-teal-400 font-semibold">카카오 채널로 제출</span>하면 완료!
                  </p>
                  <div className="bg-black/20 rounded-lg px-3 py-2.5 space-y-1.5 mb-2.5">
                    <div className="flex items-start gap-2">
                      <i className="ri-mic-2-line text-teal-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs">핵심 표현 또는 전체 대사를 <span className="text-white font-semibold">음성 녹음</span>합니다</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-send-plane-line text-teal-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs"><span className="text-white font-semibold">카카오 채널에 숙제로 전송</span>합니다</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-teal-500/10 rounded-lg px-3 py-2">
                    <i className="ri-checkbox-circle-fill text-teal-400 text-sm flex-shrink-0"></i>
                    <span className="text-teal-300 text-xs sm:text-sm font-semibold">숙제 완수 시 환급 이벤트 응모 가능!</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 자세한 진행방식 버튼 */}
      <div className="mt-8 sm:mt-10 flex justify-center">
        <button
          onClick={() => onShowHowTo()}
          className="group flex items-center gap-3 bg-white/5 hover:bg-teal-500/10 border border-gray-600 hover:border-teal-500/60 text-gray-300 hover:text-teal-300 px-8 py-4 rounded-2xl transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <div className="w-8 h-8 flex items-center justify-center bg-teal-500/20 group-hover:bg-teal-500/30 rounded-full transition-colors">
            <i className="ri-route-line text-teal-400 text-base"></i>
          </div>
          <span className="font-semibold text-sm sm:text-base">자세한 진행방식 보기</span>
          <i className="ri-arrow-right-s-line text-lg group-hover:translate-x-1 transition-transform"></i>
        </button>
      </div>

    </>
  );
}

/* ------------------------------------------------------------------ */
/*  The fragment closing tag was incorrectly written as </div> which
    caused the JSX parsing error: “Unexpected closing "div" tag does not
    match opening fragment tag”. The fix replaces the stray </div> with
    the proper fragment closing tag </>. This restores valid JSX
    structure without altering component logic. */
/* ------------------------------------------------------------------ */

function HowToModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto pt-12 pb-8 px-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[#111111] border border-gray-700 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden my-auto">
        {/* 헤더 */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-5 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-route-line text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">자세한 진행방식</h2>
              <p className="text-teal-100 text-sm">결제부터 숙제 제출까지 한눈에</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-6 py-6">
          {/* 연결선 */}
          <div className="relative">
            <div className="absolute left-[19px] top-8 bottom-8 w-0.5 bg-gradient-to-b from-teal-500/60 via-teal-500/30 to-teal-500/10 z-0"></div>

            <div className="space-y-5 relative z-10">

              {/* Step 1 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <i className="ri-bank-card-line text-white text-base"></i>
                </div>
                <div className="flex-1 bg-white/5 border border-gray-800 hover:border-teal-500/40 rounded-xl p-4 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-teal-400 text-xs font-bold bg-teal-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">STEP 1</span>
                    <h3 className="text-sm font-bold text-white">수강료 송금</h3>
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed mb-2">신한은행 계좌로 수강료를 입금합니다.</p>
                  <div className="bg-black/30 rounded-lg px-3 py-2.5 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <i className="ri-bank-line text-teal-400 text-xs flex-shrink-0"></i>
                      <span className="text-gray-300 text-xs">신한은행 <span className="text-white font-semibold">110621532440</span> (예금주: 이송희)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-information-line text-teal-400 text-xs flex-shrink-0"></i>
                      <span className="text-gray-300 text-xs">입금자명을 본인 이름으로 정확히 입력해주세요</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-[#FEE500] to-[#FEE500] rounded-full flex items-center justify-center shadow-lg shadow-yellow-500/20">
                  <i className="ri-kakao-talk-fill text-[#3A1D1D] text-base"></i>
                </div>
                <div className="flex-1 bg-white/5 border border-gray-800 hover:border-yellow-500/40 rounded-xl p-4 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-yellow-400 text-xs font-bold bg-yellow-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">STEP 2</span>
                    <h3 className="text-sm font-bold text-white">카카오 채널 추가 &amp; 입금 알림</h3>
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed mb-2">LEXO 일본어 카카오 채널을 추가하고, 입금 완료 메시지를 보내주세요.</p>
                  <div className="bg-[#FEE500]/10 border border-[#FEE500]/20 rounded-lg px-3 py-2.5 space-y-1.5">
                    <div className="flex items-start gap-2">
                      <i className="ri-chat-1-line text-yellow-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs">카카오톡에서 <span className="text-white font-semibold">"LEXO 일본어"</span> 채널 검색 후 추가</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-message-2-line text-yellow-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs">채널에 <span className="text-white font-semibold">이름 / 선택 플랜 / 입금 금액</span>을 메시지로 전송</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-time-line text-yellow-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs">입금 확인은 영업일 기준으로 처리됩니다</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-gray-600 to-gray-700 rounded-full flex items-center justify-center shadow-lg shadow-gray-500/20">
                  <i className="ri-links-line text-white text-base"></i>
                </div>
                <div className="flex-1 bg-white/5 border border-gray-800 hover:border-gray-500/40 rounded-xl p-4 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-gray-300 text-xs font-bold bg-white/10 px-2 py-0.5 rounded-full whitespace-nowrap">STEP 3</span>
                    <h3 className="text-sm font-bold text-white">학습 링크 수신</h3>
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed mb-2">
                    입금 확인 후 <span className="text-teal-400 font-semibold">밤 12시 전후</span>를 기준으로 학습 링크를 발송해드립니다.
                  </p>
                  <div className="bg-black/30 rounded-lg px-3 py-2.5 space-y-1.5 mb-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-links-line text-gray-300 text-xs flex-shrink-0"></i>
                      <span className="text-gray-300 text-xs">카카오 채널로 <span className="text-white font-semibold">학습 페이지 링크</span>가 전송됩니다</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-file-list-3-line text-gray-300 text-xs flex-shrink-0"></i>
                      <span className="text-gray-300 text-xs">학습 페이지에는 <span className="text-white font-semibold">유튜브 영상 링크 + 핵심 표현 + 대사</span>가 정리되어 있습니다</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-lock-unlock-line text-gray-300 text-xs flex-shrink-0"></i>
                      <span className="text-gray-300 text-xs">링크는 열람 시작일부터 구독 기간 동안 유효합니다</span>
                    </div>
                  </div>
                  {/* 예시 이미지 */}
                  <div className="rounded-xl overflow-hidden border border-gray-700/60">
                    <div className="bg-gray-800/80 px-3 py-2 flex items-center gap-2 border-b border-gray-700/60">
                      <div className="flex gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-red-500/70"></div>
                        <div className="w-2 h-2 rounded-full bg-yellow-500/70"></div>
                        <div className="w-2 h-2 rounded-full bg-green-500/70"></div>
                      </div>
                      <div className="flex-1 bg-gray-700/60 rounded-md px-2 py-1 flex items-center gap-1.5">
                        <i className="ri-links-line text-gray-400 text-xs"></i>
                        <span className="text-gray-400 text-xs">LEXO 일본어 학습 자료</span>
                      </div>
                    </div>
                    <div className="relative w-full" style={{ height: '160px' }}>
                      <img
                        src="https://readdy.ai/api/search-image?query=Beautiful%20attractive%20young%20Asian%20woman%20sitting%20at%20desk%20watching%20YouTube%20video%20on%20laptop%20screen%20studying%20Japanese%20language%20elegant%20stylish%20appearance%20long%20dark%20hair%20soft%20studio%20lighting%20cozy%20modern%20room%20setup%20educational%20content%20creator%20vibe%20warm%20teal%20accent%20lighting&width=800&height=440&seq=lexo-youtube-beauty-study-v2&orientation=landscape"
                        alt="학습 링크 페이지 예시"
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1">
                          <i className="ri-eye-line text-teal-400 text-xs"></i>
                          <span className="text-white text-xs font-semibold">학습 페이지 예시</span>
                        </div>
                        <div className="flex gap-1">
                          <div className="bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center gap-1">
                            <i className="ri-youtube-fill text-red-400 text-xs"></i>
                            <span className="text-gray-300 text-xs">영상 링크</span>
                          </div>
                          <div className="bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center gap-1">
                            <i className="ri-book-2-line text-teal-400 text-xs"></i>
                            <span className="text-gray-300 text-xs">핵심 표현</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-900/80 px-4 py-2.5 grid grid-cols-3 divide-x divide-gray-700/50">
                      <div className="flex flex-col items-center gap-1 px-2">
                        <i className="ri-youtube-fill text-red-400 text-sm"></i>
                        <span className="text-gray-400 text-xs text-center">유튜브 영상 링크</span>
                      </div>
                      <div className="flex flex-col items-center gap-1 px-2">
                        <i className="ri-translate-2 text-teal-400 text-sm"></i>
                        <span className="text-gray-400 text-xs text-center">핵심 표현 &amp; 대사</span>
                      </div>
                      <div className="flex flex-col items-center gap-1 px-2">
                        <i className="ri-mic-line text-orange-400 text-sm"></i>
                        <span className="text-gray-400 text-xs text-center">쉐도잉 연습</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs mt-1.5 text-center">* 실제 학습 페이지 구성 예시입니다</p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg shadow-red-500/20">
                  <i className="ri-youtube-fill text-white text-base"></i>
                </div>
                <div className="flex-1 bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/40 rounded-xl p-4 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-red-400 text-xs font-bold bg-red-500/20 px-2 py-0.5 rounded-full whitespace-nowrap">STEP 4</span>
                    <h3 className="text-sm font-bold text-white">유튜브 영상 &amp; 핵심 표현 학습</h3>
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed mb-2">영상 링크와 핵심 표현을 확인하고 학습합니다.</p>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5 space-y-1.5">
                    <div className="flex items-start gap-2">
                      <i className="ri-play-circle-line text-red-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs"><span className="text-white font-semibold">유튜브 영상</span>을 반복 시청하며 전체 흐름을 파악합니다</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-book-2-line text-red-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs">정리된 <span className="text-white font-semibold">핵심 표현과 대사</span>를 꼼꼼히 읽고 의미를 파악합니다</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-repeat-line text-red-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs"><span className="text-white font-semibold">따라 말하기(쉐도잉)</span>를 반복 연습합니다</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <i className="ri-mic-line text-white text-base"></i>
                </div>
                <div className="flex-1 bg-gradient-to-br from-teal-500/10 to-teal-600/5 border border-teal-500/40 rounded-xl p-4 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-teal-300 text-xs font-bold bg-teal-500/20 px-2 py-0.5 rounded-full whitespace-nowrap">STEP 5</span>
                    <h3 className="text-sm font-bold text-white">쉐도잉 녹음 &amp; 숙제 제출</h3>
                  </div>
                  <p className="text-gray-300 text-xs leading-relaxed mb-2">
                    학습한 내용을 쉐도잉하여 녹음한 뒤, <span className="text-teal-400 font-semibold">카카오 채널로 제출</span>하면 완료!
                  </p>
                  <div className="bg-black/20 rounded-lg px-3 py-2.5 space-y-1.5 mb-2.5">
                    <div className="flex items-start gap-2">
                      <i className="ri-mic-2-line text-teal-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs">핵심 표현 또는 전체 대사를 <span className="text-white font-semibold">음성 녹음</span>합니다</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-send-plane-line text-teal-400 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-300 text-xs"><span className="text-white font-semibold">카카오 채널에 숙제로 전송</span>합니다</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-teal-500/10 rounded-lg px-3 py-2">
                    <i className="ri-checkbox-circle-fill text-teal-400 text-sm flex-shrink-0"></i>
                    <span className="text-teal-300 text-xs font-semibold">숙제 완수 시 환급 이벤트 응모 가능!</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <button
            onClick={onClose}
            className="mt-6 w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold py-3 rounded-xl text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            확인했습니다
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Personal() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [scrolled, setScrolled] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHowToModal, setShowHowToModal] = useState(false);

  const handleStartClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setShowPaymentModal(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const personalPlans = [
    {
      name: 'Basic',
      price: '₩59,000',
      features: [
        '유튜브 링크 제공',
        '영상 속 핵심 표현 제공',
        '쉐도잉 숙제 제공'
      ],
      popular: false,
      extra: null,
    },
    {
      name: 'Smart',
      price: '₩98,000',
      features: [
        'Basic 플랜의 모든 서비스',
        'AI 작문 첨삭 제공'
      ],
      popular: false,
      extra: {
        label: 'Smart 전용 추가 서비스',
        items: [
          { icon: 'ri-robot-line', text: 'AI 작문 첨삭 — 내가 쓴 일본어 문장을 AI가 교정' },
          { icon: 'ri-edit-2-line', text: '매 회차 작문 미션 제공으로 쓰기 실력 강화' },
        ]
      }
    },
    {
      name: 'Pro',
      price: '₩128,000',
      features: [
        'Smart 플랜의 모든 서비스',
        'PDF 자료 제공',
        'PDF 문제 풀이 제공'
      ],
      popular: true,
      extra: {
        label: 'Pro 전용 추가 서비스',
        items: [
          { icon: 'ri-file-pdf-line', text: 'PDF 학습 자료 — 핵심 표현·문법 정리 PDF 제공' },
          { icon: 'ri-questionnaire-line', text: 'PDF 문제 풀이 — 복습 문제 & 해설 PDF 제공' },
          { icon: 'ri-robot-line', text: 'AI 작문 첨삭 포함 (Smart 플랜 서비스 전체 포함)' },
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {showPaymentModal && <PaymentModal onClose={() => setShowPaymentModal(false)} onShowHowTo={() => setShowHowToModal(true)} />}
      {showHowToModal && <HowToModal onClose={() => setShowHowToModal(false)} />}
      <Navbar scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-teal-500/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-4 whitespace-nowrap">
              개인 학습자용
            </div>
            <h1 className="text-[36px] sm:text-[60px] md:text-[80px] font-bold text-white mb-4 sm:mb-6">
              LEXO 강한 일본어 : <span className="bg-gradient-to-r from-teal-400 to-teal-500 bg-clip-text text-transparent">Personal</span>
            </h1>
            <p className="text-sm sm:text-xl text-gray-300 max-w-3xl mx-auto mb-6 sm:mb-8 px-2">
              컨텐츠는 일본의 유명 방송프로그램, 애니메이션 등 랜덤 발송
            </p>
            <button
              onClick={handleStartClick}
              className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold px-8 sm:px-12 py-3 sm:py-4 rounded-xl text-sm sm:text-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Personal 플랜 시작하기
            </button>
          </div>
        </div>
      </section>

      {/* 학습 진행 과정 섹션 */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-teal-500/20 text-teal-400 px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
              학습 진행 과정
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
              이렇게 진행됩니다
            </h2>
            <p className="text-gray-400 text-sm sm:text-lg">결제부터 학습까지 간단한 3단계</p>
          </div>
          
          <div className="grid grid-cols-3 gap-2 sm:gap-8 mb-10 sm:mb-16">
            {/* Step 1 */}
            <div className="relative group">
              <div className="absolute -top-2 sm:-top-4 -left-1 sm:-left-4 w-6 h-6 sm:w-12 sm:h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-xl z-10">
                1
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300">
                <div className="w-full h-20 sm:h-48 overflow-hidden" style={{ minHeight: '80px' }}>
                  <img 
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20payment%20checkout%20screen%20on%20smartphone%20with%20credit%20card%20and%20secure%20payment%20icons%20teal%20accent%20clean%20white%20background%20professional%20fintech%20design%20simple%20elegant%20interface&width=400&height=300&seq=lexo-payment-step-001&orientation=landscape"
                    alt="결제하기"
                    loading="eager"
                    crossOrigin="anonymous"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    style={{ display: 'block', width: '100%', height: '100%' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <div className="p-2 sm:p-6">
                  <h3 className="text-xs sm:text-xl font-bold text-white mb-1 sm:mb-2">플랜 선택 &amp; 결제</h3>
                  <p className="text-gray-400 text-[10px] sm:text-[15px] hidden sm:block">원하는 플랜을 선택하고 간편하게 결제하세요</p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="absolute -top-2 sm:-top-4 -left-1 sm:-left-4 w-6 h-6 sm:w-12 sm:h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-xl z-10">
                2
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300">
                <div className="w-full h-20 sm:h-48 overflow-hidden" style={{ minHeight: '80px' }}>
                  <img 
                    src="https://readdy.ai/api/search-image?query=KakaoTalk%20messenger%20app%20interface%20showing%20friend%20add%20button%20yellow%20chat%20bubble%20icon%20smartphone%20screen%20clean%20modern%20design%20Korean%20messaging%20app%20simple%20minimalist%20style&width=400&height=300&seq=lexo-kakao-add-002&orientation=landscape"
                    alt="카카오톡 친구추가"
                    loading="eager"
                    crossOrigin="anonymous"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    style={{ display: 'block', width: '100%', height: '100%' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <div className="p-2 sm:p-6">
                  <h3 className="text-xs sm:text-xl font-bold text-white mb-1 sm:mb-2">플러스친구 추가</h3>
                  <p className="text-gray-400 text-[10px] sm:text-[15px] hidden sm:block">"LEXO 일본어" 플러스친구를 추가해주세요</p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="absolute -top-2 sm:-top-4 -left-1 sm:-left-4 w-6 h-6 sm:w-12 sm:h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-xl z-10">
                3
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300">
                <div className="w-full h-20 sm:h-48 overflow-hidden" style={{ minHeight: '80px' }}>
                  <img 
                    src="https://readdy.ai/api/search-image?query=Japanese%20language%20learning%20content%20on%20smartphone%20screen%20showing%20anime%20scene%20with%20Japanese%20subtitles%20and%20vocabulary%20highlights%20teal%20accent%20educational%20app%20interface%20modern%20clean%20design&width=400&height=300&seq=lexo-learning-content-003&orientation=landscape"
                    alt="학습 시작"
                    loading="eager"
                    crossOrigin="anonymous"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    style={{ display: 'block', width: '100%', height: '100%' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <div className="p-2 sm:p-6">
                  <h3 className="text-xs sm:text-xl font-bold text-white mb-1 sm:mb-2">학습 시작</h3>
                  <p className="text-gray-400 text-[10px] sm:text-[15px] hidden sm:block">학습 링크가 발송되면 바로 학습을 시작하세요</p>
                </div>
              </div>
            </div>
          </div>

          {/* 발송 시작 안내 */}
          <div className="bg-gradient-to-r from-teal-500/10 to-teal-600/10 border border-teal-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-8">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-500/20 rounded-full flex items-center justify-center">
                <i className="ri-time-line text-teal-400 text-base sm:text-xl"></i>
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white">발송 시작 안내</h3>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-6">
              <div className="bg-black/20 rounded-lg sm:rounded-xl p-3 sm:p-5">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                  <i className="ri-sun-line text-yellow-400 text-sm flex-shrink-0 mt-0.5"></i>
                  <span className="text-white font-semibold text-xs sm:text-base">밤 12시 이전 결제</span>
                </div>
                <p className="text-gray-300 text-[11px] sm:text-[15px]">
                  <span className="text-teal-400 font-semibold">영업일 기준 다음날</span>부터 발송
                </p>
              </div>
              <div className="bg-black/20 rounded-lg sm:rounded-xl p-3 sm:p-5">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                  <i className="ri-moon-line text-indigo-400 text-sm flex-shrink-0 mt-0.5"></i>
                  <span className="text-white font-semibold text-xs sm:text-base">밤 12시 이후 결제</span>
                </div>
                <p className="text-gray-300 text-[11px] sm:text-[15px]">
                  <span className="text-teal-400 font-semibold">영업일 기준 다다음날</span>부터 발송
                </p>
              </div>
            </div>
            <p className="text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4 text-center">
              * 영업일: 월~금 (공휴일 제외)
            </p>
          </div>

          {/* 자세한 진행방식 버튼 제거 (플랜 하단으로 이동) */}
        </div>
      </section>

      {/* 콘텐츠 소개 섹션 */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-red-500/20 text-red-400 px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
              학습 콘텐츠
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
              이런 콘텐츠로 배워요
            </h2>
            <p className="text-gray-400 text-sm sm:text-lg">일본의 인기 방송프로그램 &amp; 애니메이션</p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-8">
            {/* 애니메이션 */}
            <div className="group relative rounded-xl sm:rounded-2xl overflow-hidden" style={{ minHeight: '140px' }}>
              <div className="w-full h-[140px] sm:h-[300px]" style={{ minHeight: '140px' }}>
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20Japanese%20anime%20scene%20romantic%20sunset%20cityscape%20with%20cherry%20blossoms%20Your%20Name%20Kimi%20no%20Na%20wa%20style%20vibrant%20colors%20emotional%20atmosphere%20detailed%20background%20art%20studio%20quality%20animation&width=600&height=400&seq=lexo-anime-content-004&orientation=landscape"
                  alt="애니메이션 콘텐츠"
                  loading="eager"
                  crossOrigin="anonymous"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  style={{ display: 'block', width: '100%', height: '100%' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6">
                <div className="inline-block bg-pink-500/80 text-white px-2 sm:px-4 py-0.5 sm:py-1.5 rounded-full text-[10px] sm:text-sm font-semibold mb-1 sm:mb-3 whitespace-nowrap">
                  애니메이션
                </div>
                <h3 className="text-sm sm:text-2xl font-bold text-white mb-0.5 sm:mb-2">인기 일본 애니메이션</h3>
                <p className="text-gray-300 text-[10px] sm:text-[15px]">
                  <span className="text-teal-400 font-semibold">인기 애니메이션의 실제 대사로</span> 자연스러운 일본어 표현 학습
                </p>
              </div>
            </div>

            {/* 방송프로그램 */}
            <div className="group relative rounded-xl sm:rounded-2xl overflow-hidden" style={{ minHeight: '140px' }}>
              <div className="w-full h-[140px] sm:h-[300px]" style={{ minHeight: '140px' }}>
                <img 
                  src="https://readdy.ai/api/search-image?query=Japanese%20variety%20TV%20show%20studio%20set%20with%20colorful%20lighting%20entertainment%20program%20stage%20modern%20broadcast%20studio%20professional%20television%20production%20vibrant%20atmosphere&width=600&height=400&seq=lexo-tv-content-005&orientation=landscape"
                  alt="방송프로그램 콘텐츠"
                  loading="eager"
                  crossOrigin="anonymous"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  style={{ display: 'block', width: '100%', height: '100%' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6">
                <div className="inline-block bg-orange-500/80 text-white px-2 sm:px-4 py-0.5 sm:py-1.5 rounded-full text-[10px] sm:text-sm font-semibold mb-1 sm:mb-3 whitespace-nowrap">
                  방송프로그램
                </div>
                <h3 className="text-sm sm:text-2xl font-bold text-white mb-0.5 sm:mb-2">인기 일본 방송프로그램</h3>
                <p className="text-gray-300 text-[10px] sm:text-[15px]">
                  <span className="text-teal-400 font-semibold">실제 일본인이 사용하는 생생한 회화</span> 표현 학습
                </p>
              </div>
            </div>
          </div>
          
          <p className="text-gray-500 text-xs sm:text-sm text-center mt-4 sm:mt-6">
            * 콘텐츠 내용은 랜덤입니다
          </p>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-2 sm:mb-4">플랜 선택</h2>
            <p className="text-gray-400 text-sm sm:text-lg">나에게 맞는 플랜을 선택하세요</p>
            <p className="text-gray-500 text-xs sm:text-sm mt-2">* 모든 가격은 부가세 별도입니다</p>
          </div>
          
          <div className="flex flex-col gap-5 sm:gap-8">
            {personalPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-[1px] sm:p-[2px] rounded-xl sm:rounded-2xl ${
                  plan.popular ? 'ring-2 ring-teal-500/60' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 sm:px-6 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap z-10">
                    인기
                  </div>
                )}
                <div className="bg-[#0A0A0A] rounded-xl sm:rounded-2xl p-5 sm:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
                    <div>
                      <h3 className="text-xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">{plan.name}</h3>
                      <div className="flex items-baseline gap-1 flex-wrap">
                        <span className="text-2xl sm:text-4xl font-bold text-white">{plan.price}</span>
                        <span className="text-gray-500 text-[10px] sm:text-xs ml-1 whitespace-nowrap">(부가세 별도)</span>
                      </div>
                    </div>
                    <button
                      onClick={handleStartClick}
                      className="mt-4 sm:mt-0 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold py-3 sm:py-4 px-8 sm:px-10 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                    >
                      시작하기
                    </button>
                  </div>
                  <div className="border-t border-gray-800 pt-4 sm:pt-6">
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-300">
                          <i className="ri-check-line text-teal-400 text-base sm:text-lg mr-2.5 sm:mr-3 flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center"></i>
                          <span className="text-sm sm:text-base leading-snug">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 자세한 진행방식 버튼 - 플랜 하단 */}
          <div className="mt-8 sm:mt-10 flex justify-center">
            <button
              onClick={() => setShowHowToModal(true)}
              className="group flex items-center gap-3 bg-white/5 hover:bg-teal-500/10 border border-gray-600 hover:border-teal-500/60 text-gray-300 hover:text-teal-300 px-8 py-4 rounded-2xl transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-teal-500/20 group-hover:bg-teal-500/30 rounded-full transition-colors">
                <i className="ri-route-line text-teal-400 text-base"></i>
              </div>
              <span className="font-semibold text-sm sm:text-base">자세한 진행방식 보기</span>
              <i className="ri-arrow-right-s-line text-lg group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>
        </div>
      </section>

      {/* 강한 일본어 Slogan Banner */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden" style={{ minHeight: '300px' }}>
            <div className="w-full h-[300px] sm:h-[600px]">
              <img
                src="https://readdy.ai/api/search-image?query=Dramatic%20intense%20Japanese%20language%20learning%20motivation%20banner%20with%20powerful%20explosive%20fire%20flames%20and%20energy%20bursts%20featuring%20bold%20strong%20red%20crimson%20scarlet%20color%20scheme%20with%20dynamic%20flame%20particles%20embers%20and%20dramatic%20lighting%20effects%20emphasizing%20strength%20power%20and%20passion%20with%20text%20space%20for%20strong%20Japanese%20slogan%20professional%20education%20design&width=1400&height=600&seq=lexo-strong-japanese-red-fire-003&orientation=landscape"
                alt="강한 일본어"
                loading="eager"
                crossOrigin="anonymous"
                className="w-full h-full object-cover object-center"
                style={{ display: 'block', width: '100%', height: '100%' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
              <div className="inline-block bg-red-600/90 backdrop-blur-sm text-white px-4 sm:px-8 py-1.5 sm:py-3 rounded-full text-[10px] sm:text-sm font-bold mb-4 sm:mb-8 border-2 border-red-400 whitespace-nowrap">
                LEXO의 약속
              </div>
              <h2 className="text-3xl sm:text-6xl md:text-8xl font-black text-white mb-3 sm:mb-6 drop-shadow-2xl">
                강한 일본어
              </h2>
              <p className="text-base sm:text-2xl md:text-3xl text-white font-bold mb-4 sm:mb-8 drop-shadow-lg">
                진짜 실력으로 승부하는 일본어 학습
              </p>
              <button
                onClick={handleStartClick}
                className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 sm:px-12 py-3 sm:py-5 rounded-xl text-sm sm:text-lg transition-all duration-300 shadow-2xl border-2 border-red-400 whitespace-nowrap cursor-pointer"
              >
                지금 시작하기
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-4xl font-bold text-white text-center mb-10 sm:mb-16">
            자주 묻는 질문
          </h2>
          <div className="space-y-3 sm:space-y-6">
            {[
              {
                q: '플랜은 언제든지 변경할 수 있나요?',
                a: '네, 언제든지 플랜을 변경할 수 있습니다. 플랜 변경 시 다음 달부터 적용됩니다.'
              },
              {
                q: '환불 정책은 어떻게 되나요?',
                a: '결제 후 7일 이내 콘텐츠 미사용 시 환불 가능합니다. 단, 부가세 및 카드 수수료는 환불되지 않습니다. 콘텐츠를 이용한 경우에는 환불이 불가합니다.'
              },
              {
                q: '어떤 콘텐츠로 학습하나요?',
                a: '일본의 유명 방송프로그램, 애니메이션 등을 랜덤으로 제공하여 다양한 실전 일본어를 학습할 수 있습니다.'
              },
              {
                q: '학습 자료는 언제 발송되나요?',
                a: '정기적으로 발송됩니다. 밤 12시 이전 결제시 영업일 기준 다음날부터, 밤 12시 이후 결제시 영업일 기준 다다음날부터 발송됩니다.'
              },
              {
                q: '문의는 어떻게 하나요?',
                a: '모든 문의 사항은 lexojp.help@gmail.com 으로 연락주시면 신속하게 답변드리겠습니다.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-xl p-4 sm:p-6 hover:bg-white/10 transition-all duration-300">
                <h3 className="text-sm sm:text-lg font-bold text-white mb-2 sm:mb-3">{faq.q}</h3>
                <p className="text-gray-400 text-xs sm:text-base">{faq.a}</p>
              </div>
            ))}
          </div>

          {/* 문의 및 환불 카드 - FAQ 아래로 이동 */}
          <div className="mt-8 sm:mt-12 bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-8 text-center">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <i className="ri-mail-send-line text-teal-400 text-lg sm:text-2xl"></i>
            </div>
            <h3 className="text-base sm:text-xl font-bold text-white mb-2 sm:mb-3">문의 및 환불</h3>
            <p className="text-gray-400 text-sm sm:text-base mb-3 sm:mb-4">
              문의 사항이나 환불이 필요하신 경우 아래 이메일로 연락해주세요
            </p>
            <a href="mailto:lexojp.help@gmail.com" className="text-teal-400 font-semibold text-sm sm:text-lg hover:text-teal-300 transition-colors">
              lexojp.help@gmail.com
            </a>
            <p className="text-gray-500 text-xs sm:text-sm mt-2 sm:mt-3">
              * 환불 시 부가세 및 카드 수수료는 환불되지 않습니다
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
